 import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase.js';

function MediaDetailPage({ user, isAdmin }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [error, setError] = useState(null);
  const [showDownloadPrompt, setShowDownloadPrompt] = useState(true);

  useEffect(() => {
    const fetchMedia = async () => {
      if (!user) {
        setError('Please sign in to view media');
        return;
      }
      try {
        console.log('Fetching media item:', id, 'for user:', user.uid);
        const docRef = doc(db, 'community_photos', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setMedia({ id: docSnap.id, ...data });
        } else {
          setError('Media not found');
        }
      } catch (err) {
        console.error('Error fetching media:', err);
        setError('Failed to load media: ' + err.message);
      }
    };
    fetchMedia();
  }, [id, user]);

  const handleDownload = () => {
    setShowDownloadPrompt(false);
    if (media && media.url) {
      const link = document.createElement('a');
      link.href = media.url;
      link.download = media.url.split('/').pop().split('?')[0];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCancelDownload = () => {
    setShowDownloadPrompt(false);
  };

  if (error) {
    return <div className="container error-message">{error}</div>;
  }

  if (!media) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="media-detail-container">
      <button
        onClick={() => navigate('/community-pics')}
        className="modal-button modal-button-secondary"
      >
        Back to Gallery
      </button>
      <div className="media-content">
        {media.url.includes('video') ? (
          <video src={media.url} controls className="media-full" />
        ) : (
          <img src={media.url} alt={media.caption} className="media-full" />
        )}
      </div>
      <p className="media-caption">{media.caption}</p>
      <p className="media-uploader">Uploaded by: {media.uploadedBy}</p>
      <p className="media-date">
        Uploaded: {new Date(media.uploadedAt).toLocaleDateString()}
      </p>
      {(isAdmin || media.uploaderUid === user?.uid) && (
        <div className="media-actions">
          <button
            onClick={() => navigate(`/community-pics/edit/${id}`)}
            className="modal-button modal-button-secondary"
          >
            Edit
          </button>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this media?')) {
                // Deletion logic (to be implemented if needed)
                navigate('/community-pics');
              }
            }}
            className="modal-button modal-button-danger"
          >
            Delete
          </button>
        </div>
      )}
      {showDownloadPrompt && (
        <div className="download-prompt">
          <p>Do you want to download this {media.url.includes('video') ? 'video' : 'photo'}?</p>
          <button
            onClick={handleDownload}
            className="modal-button modal-button-primary"
          >
            Download
          </button>
          <button
            onClick={handleCancelDownload}
            className="modal-button modal-button-secondary"
          >
            Cancel
          </button>
        </div>
      )}

      <style>
        {`
          .media-detail-container {
            padding: 2rem;
            background: white;
            border-radius: 0.5rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            margin: 2rem auto;
            max-width: 1200px;
            opacity: 0.9;
            text-align: center;
          }
          .media-content {
            margin: 1rem 0;
          }
          .media-full {
            width: 100%;
            max-width: 100%;
            height: auto;
            border-radius: 0.25rem;
          }
          .media-caption {
            font-size: 1.25rem;
            margin: 0.5rem 0;
            font-weight: bold;
          }
          .media-uploader {
            font-size: 1rem;
            margin: 0.25rem 0;
            color: #4b5563;
          }
          .media-date {
            font-size: 1rem;
            color: #6b7280;
            margin-bottom: 1rem;
          }
          .media-actions {
            display: flex;
            justify-content: center;
            gap: 0.5rem;
            margin-top: 1rem;
          }
          .download-prompt {
            background: #f3f4f6;
            padding: 1rem;
            border-radius: 0.5rem;
            margin-top: 1rem;
            display: inline-block;
          }
          .download-prompt p {
            margin-bottom: 0.5rem;
            font-size: 1rem;
          }
          .modal-button {
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
            border: none;
            cursor: pointer;
            font-size: 1rem;
            margin: 0 0.25rem;
          }
          .modal-button-primary {
            background: #007bff;
            color: white;
          }
          .modal-button-primary:hover {
            background: #0056b3;
          }
          .modal-button-secondary {
            background: #6c757d;
            color: white;
          }
          .modal-button-secondary:hover {
            background: #5a6268;
          }
          .modal-button-danger {
            background: #dc3545;
            color: white;
          }
          .modal-button-danger:hover {
            background: #c82333;
          }
          .error-message {
            color: #ef4444;
            font-size: 1rem;
            margin: 1rem 0;
          }
          @media (max-width: 640px) {
            .media-detail-container {
              padding: 1rem;
              margin: 1rem;
            }
            .media-caption {
              font-size: 1rem;
            }
            .media-uploader, .media-date {
              font-size: 0.875rem;
            }
            .download-prompt {
              padding: 0.75rem;
            }
            .download-prompt p {
              font-size: 0.875rem;
            }
            .modal-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.875rem;
            }
          }
        `}
      </style>
    </div>
  );
}

export default MediaDetailPage;